import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore
import styles from './CaseMessages.module.scss';
import classNames from 'classnames';
import { CaseMessagesResponse, emptyCaseMessagesModel } from './types/CaseMessagesResponse';
import { useParams } from 'react-router-dom';
import Modal from '../../../../../core/components/Modal/Modal';
import ApiProvider from '../../../../../core/api/ApiProvider';
import { ResponseApiType } from '../../../../types/ResponseApiType';
import { InfoMessageService, TranslationService } from '../../../../../core/services';
import {
  Alert,
  AreaField,
  Button,
  Form,
  Icon,
  Loader,
  ModuleContainer,
  TextField,
} from '../../../../../core/components';
import { MessageItem } from './types/MessageItem';
import styled, { css } from 'styled-components';
import { DictionaryType } from '../../../../../core/types/DictionaryType';
import { useAppContext } from '../../../../../AppContext';

const CaseMessages = () => {
  const context = useAppContext();
  const { caseNumber, tenantUId } = useParams() as { caseNumber: string; tenantUId: string };
  const convMsgLisRef = useRef<HTMLUListElement>();
  const [isFetchError, setIsFetchError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [isPaid, setIsPaid] = useState<boolean>(false);
  const [displayNoMessagesAlert, setDisplayNoMessagesAlert] = useState<boolean>(false);
  const [model, setModel] = useState<CaseMessagesResponse>(emptyCaseMessagesModel);
  const [selectedConversation, setSelectedConversation] = useState<MessageItem>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [viewMode, setViewMode] = useState<string>('list');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [msgValue, setMsgValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isMsgSending, setIsMsgSending] = useState<boolean>(false);

  const filterConversations = (conversation: MessageItem) => {
    return (
      conversation.caseNo.includes(searchValue) ||
      conversation.messageTitle.includes(searchValue) ||
      conversation.notes.some((x) => x.noteText.includes(searchValue)) ||
      conversation.sendDateFormatted.includes(searchValue)
    );
  };

  const fetchCaseMessages = async () => {
    try {
      const result = await ApiProvider.get<ResponseApiType<CaseMessagesResponse>>(
        `CaseMessagesModule/GetCaseMessages/${caseNumber}`
      );
      if (result.success) {
        setModel(result.data);

        setIsPaid(result.data.isPaid);

        if (result.data.messagesVmList.length > 0) {
          setSelectedConversation(result.data.messagesVmList[0]);

          if (!result.data.messagesVmList[0].notes.slice(-1)[0].readByDebtor) {
            NotifyCaseHandlerMsgRead(result.data.messagesVmList[0].messageId, caseNumber);
            result.data.messagesVmList[0].notes.slice(-1)[0].readByDebtor = true;
          }

          TranslationService.translate('GeneralSuccessMsg');

          scrollMessagesContainerToBottom();
          setDisplayNoMessagesAlert(false);
        } else {
          setDisplayNoMessagesAlert(true);
        }
      } else {
        throw result.message;
      }
    } catch (err) {
      setIsFetchError(true);

      InfoMessageService.error(
        (err as string) || TranslationService.translate('ErrorSthWentWrong')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const NotifyCaseHandlerMsgRead = async (msgId: string, caseNumber: string) => {
    try {
      const result = await ApiProvider.post<ResponseApiType>(
        'CaseMessagesModule/NotifyCaseHandlerMsgRead',
        { messageId: msgId, caseNo: caseNumber, sourceTenantId: tenantUId }
      );
      if (result.success) {
      }
    } catch (err) {
      InfoMessageService.error('ErrorSthWentWrong');
    } finally {
      setIsLoading(false);
      scrollMessagesContainerToBottom();
    }
  };

  const handleConvListItem = (conv: MessageItem) => {
    if (!conv.notes.slice(-1)[0].readByDebtor) {
      NotifyCaseHandlerMsgRead(conv.messageId, caseNumber);
      conv.notes.slice(-1)[0].readByDebtor = true;
    }
    setViewMode('conversation');
    setSelectedConversation(conv);
  };

  const changeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const onMessageChange = (value: string) => {
    setMessage(value);
  };

  const scrollMessagesContainerToBottom = () => {
    if (convMsgLisRef.current != null) {
      convMsgLisRef.current.scrollTop = convMsgLisRef.current.scrollHeight;
    }
  };

  const handleReplyClick = async () => {
    if (!selectedConversation) return;
    if (message.trim().length < 10) {
      const waringInformation = TranslationService.translate(
        'NewMessage_ErrorMessageMinMessageLength'
      );
      InfoMessageService.warning(waringInformation.replace('{0}', '10'));
    } else {
      setIsReplying(true);
      try {
        const result = await ApiProvider.post<ResponseApiType>(
          'CaseMessagesModule/CaseMessageReply',
          {
            messageId: selectedConversation!.messageId,
            noteText: message,
            sourceTenantId: tenantUId,
            caseNo: caseNumber,
          }
        );

        if (result.success) {
          setMessage('');

          setSelectedConversation({
            ...selectedConversation,
            notes: [...selectedConversation.notes, result.data],
          });

          scrollMessagesContainerToBottom();

          TranslationService.translate(TranslationService.translate('GeneralSuccessMsg'));
        }
      } catch (err) {
        InfoMessageService.error('ErrorSthWentWrong');
      } finally {
        setIsReplying(false);
      }
    }
  };

  const handleBackToMessages = () => {
    setViewMode('list');
  };

  const handleAddNewMessage = () => {
    setIsOpen(true);
  };

  const handleCancelNewMessage = () => {
    setIsOpen(false);
  };

  const handleSubmitNewMessage = async () => {
    if (msgValue.trim().length < 10) {
      const waringInformation = TranslationService.translate(
        'NewMessage_ErrorMessageMinMessageLength'
      );
      InfoMessageService.warning(waringInformation.replace('{0}', '10'));
    } else {
      try {
        setIsMsgSending(true);
        const result = await ApiProvider.post<ResponseApiType<CaseMessagesResponse>>(
          'CaseMessagesModule/CreateCaseMessage',
          {
            CaseNo: caseNumber,
            tenantUId,
            message: msgValue,
          }
        );

        if (result.success) {
          InfoMessageService.success(result.message);
          await fetchCaseMessages();
        } else {
          InfoMessageService.error(result.message);
        }
      } catch (err) {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      } finally {
        setIsMsgSending(false);
        setIsOpen(false);
      }
    }
  };

  const onToggle = () => {
    setIsOpen(false);
  };

  const onNewMessageChange = (value: string) => {
    if (value.trim().length === 0) {
      setIsValid(false);
      setError(TranslationService.translate('WrongValue'));
    } else if (value.trim().length < 10) {
      const waringInformation = TranslationService.translate(
        'NewMessage_ErrorMessageMinMessageLength'
      );
      setIsValid(false);
      setError(waringInformation.replace('{0}', '10'));
    } else if (value.trim().length > 7999) {
      setIsValid(false);
      setError(TranslationService.translate('MsgMaxLength'));
    } else {
      setIsValid(true);
    }

    setMsgValue(value);
  };

  useEffect(() => {
    fetchCaseMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : displayNoMessagesAlert ? (
        <>
          <Alert
            className="mb-0"
            variant={Alert.Variants.Info}
            text={TranslationService.translate('NoNewMsgs')}
          />

          <div className={styles.footer}>
            <Button
              variant={Button.Variants.Primary}
              type={Button.Types.Button}
              onClick={handleAddNewMessage}
              disabled={isPaid}
            >
              {TranslationService.translate('NewMessage')}
            </Button>
          </div>
        </>
      ) : (
        !isFetchError && (
          <>
            <div className="row">
              <div
                className={classNames(
                  'col-12 col-sm-6',
                  viewMode === 'conversation' && styles.hideOnMobile
                )}
              >
                <Form.Group>
                  <TextField
                    name="search"
                    onChange={changeSearchValue}
                    placeholder={TranslationService.translate('Search')}
                  />
                </Form.Group>
                <div className={styles.convsListScroll}>
                  <ul
                    className={styles.convsList}
                    aria-label={TranslationService.translate('Messages')}
                  >
                    {model.messagesVmList.filter(filterConversations).map((c) => (
                      <li
                        key={c.messageId}
                        className={classNames(
                          styles.convsListItem,
                          c.messageId === selectedConversation!.messageId &&
                            styles.convsListItemActive,
                          !c.isRead && styles.newMessage
                        )}
                      >
                        <button
                          type="button"
                          className={styles.convsListItemBtn}
                          onClick={() => handleConvListItem(c)}
                        >
                          <span className={classNames('col-4', 'col-md-3', 'col-sm-2', styles.msgItem)}>
                            {c.sendDateFormatted}
                          </span>
                          <span className={classNames('col-12', 'col-sm-9', styles.msgItem)}>
                            {c.messageTitle}
                          </span>
                          <span
                            className={classNames('col-2', 'col-sm-1', styles.msgItem, styles.msgIcon)}
                          >
                            <Icon
                              iconVariant={Icon.Variants.FontAwesome}
                              icon="fas fa-angle-right"
                              className={styles.progressIcon}
                            />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.footer}>
                  <Button
                    variant={Button.Variants.Primary}
                    type={Button.Types.Button}
                    onClick={handleAddNewMessage}
                  >
                    {TranslationService.translate('NewMessage')}
                  </Button>
                </div>
              </div>
              <div
                className={classNames(
                  'col-12 col-sm-6',
                  viewMode === 'list' && styles.hideOnMobile
                )}
              >
                {isReplying && <Loader opacity={0.4} />}

                <div className={styles.conversationWrapper}>
                  <Button
                    variant={Button.Variants.Secondary}
                    type={Button.Types.Button}
                    classNames={styles.backToMessagesBtn}
                    onClick={handleBackToMessages}
                  >
                    <Icon
                      iconVariant={Icon.Variants.FontAwesome}
                      icon="fas fa-arrow-left"
                      className="mr-1"
                    />
                    {TranslationService.translate('Back')}
                  </Button>
                  {/* @ts-ignore */}
                  <ul className={styles.convMsgLis} ref={convMsgLisRef}>
                    {selectedConversation &&
                      selectedConversation.notes.map((n, index) => (
                        <MessageContent
                          key={`${n.noteCreatorName}${index}`}
                          styles={context.styles}
                          className={classNames(
                            styles.convMsg,
                            n.noteDirection === 'ToCaseHandlerFromDebtor'
                              ? styles.msgSend
                              : styles.msgReceived
                          )}
                        >
                          <div className={styles.convMsgHeader}>
                            <div>{n.noteCreatorName}</div>
                            <div>{n.noteSendDateFormatted}</div>
                          </div>

                          {n.noteText}
                        </MessageContent>
                      ))}
                  </ul>

                  <ConversationFooter styles={context.styles} className={styles.conversationFooter}>
                    <AreaField
                      name="contentMessage"
                      className={styles.areaFieldMsg}
                      value={message}
                      onChange={onMessageChange}
                      placeholder={TranslationService.translate(
                        'NewMessage_WriteMessagePlaceholder'
                      )}
                    />

                    <div className="row">
                      <div className="col-auto">
                        <Button
                          variant={Button.Variants.Secondary}
                          type={Button.Types.Button}
                          onClick={handleReplyClick}
                        >
                          {TranslationService.translate('Send')}
                        </Button>
                      </div>
                    </div>
                  </ConversationFooter>
                </div>
              </div>
            </div>
          </>
        )
      )}

      <Modal
        isOpen={isOpen}
        onToggle={onToggle}
        className={styles.modalWrapper}
        closeBtnClass="modal-close-btn"
      >
        <ModuleContainer noMargin={true} variant={ModuleContainer.ModuleVariant.Box}>
          <ModuleContainer.Header>
            {TranslationService.translate('NewMessage')}
          </ModuleContainer.Header>

          <ModuleContainer.Body>
            {isMsgSending && <Loader opacity={0.4} />}
            <AreaField
              name="noteText"
              cols={30}
              rows={4}
              className={styles.noteText}
              onChange={onNewMessageChange}
              placeholder={TranslationService.translate('NewMessage_WriteMessagePlaceholder')}
            />
            {!isValid && <div className="error-field">{error}</div>}

            <div className={styles.modalFooter}>
              <div className="row">
                <div className="col-auto">
                  <Button
                    variant={Button.Variants.Secondary}
                    classNames={classNames(styles.cancelButton)}
                    type={Button.Types.Button}
                    onClick={handleCancelNewMessage}
                  >
                    {TranslationService.translate('Cancel')}
                  </Button>
                </div>

                <div className="col-auto">
                  <Button
                    variant={Button.Variants.Secondary}
                    type={Button.Types.Button}
                    classNames={classNames(styles.sendButton)}
                    onClick={handleSubmitNewMessage}
                  >
                    {TranslationService.translate('Send')}
                  </Button>
                </div>
              </div>
            </div>
          </ModuleContainer.Body>
        </ModuleContainer>
      </Modal>
    </>
  );
};

const ConversationFooter = styled.form<{
  styles: DictionaryType<DictionaryType<string>>;
}>`
  ${(props) => {
    let styles = '';
    const messageModule = props.styles['module:messages'];

    if (props.styles) {
      const brandColors = props.styles['general:colors'].brand;
      let bgColor = '';

      if (messageModule && messageModule.backgroundColor) {
        bgColor = messageModule.backgroundColor;
      } else if (brandColors) {
        bgColor = brandColors.split(',')[0];
      }

      if (bgColor.length) {
        styles = `background: ${bgColor} !important; ${styles}`;
      }

      if (messageModule && messageModule.textColor) {
        styles = `color: ${messageModule.textColor} !important; ${styles}`;
      }
    }

    return css`
      ${styles}
    `;
  }}
`;

const MessageContent = styled.li<{
  styles: DictionaryType<DictionaryType<string>>;
}>`
  ${(props) => {
    let styles = '';
    const messagesReceivedModule = props.styles['module:messages:received'];

    if (props.styles && messagesReceivedModule) {
      if (messagesReceivedModule.backgroundColor) {
        styles = `background: ${props.styles['module:messages:received'].backgroundColor} !important; ${styles}`;
      }

      if (messagesReceivedModule.textColor) {
        styles = `color: ${props.styles['module:messages'].textColor} !important; ${styles}`;
      }
    }

    return css`
      ${styles}
    `;
  }}
`;

export default CaseMessages;
